import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Clock, ArrowRight, Mail } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { CONTACT } from "../../data/content";
import CareerApplicationForm from "../../components/site/CareerApplicationForm";

const OPENINGS = [
  { slug: "field-engineer", title: "Field Engineer — Infrastructure & Surveillance", location: "Ernakulam · field work across Kerala", type: "Full-time", text: "Install, configure and commission CCTV, access control and network systems at client sites across Kerala. Hands-on work, documented standards." },
  { slug: "network-engineer", title: "Network & Systems Engineer", location: "Ernakulam · hybrid", type: "Full-time", text: "Design and support enterprise networks, Wi-Fi, firewalls and server environments — from survey to managed operations." },
  { slug: "support-engineer", title: "Technical Support Engineer — AMC & Managed Services", location: "Ernakulam", type: "Full-time", text: "Own the long-term relationship: preventive maintenance, remote support and on-site response for our AMC and managed-services clients." },
];

const WHY = [
  { title: "Real engineering", text: "Design-led work with documentation, not ad-hoc installations." },
  { title: "All of Kerala", text: "Sites across fourteen districts — offices, hospitals, hotels, campuses." },
  { title: "Growth with the company", text: "Early team members shape how NexSecure builds, delivers and supports." },
];

const Careers = () => {
  const [showAppForm, setShowAppForm] = useState(false);
  const location = useLocation();

  // Deep links like /careers#apply-now should land on the "Join the NexSecure Team" block
  useEffect(() => {
    if (location.hash === "#apply-now") {
      const t = setTimeout(() => {
        document.getElementById("apply-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.hash]);

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="careers-page">
      <SiteHeader />
      <PageHero
        crumb="Careers"
        eyebrow="Careers"
        title="Build infrastructure"
        titleAccent="that outlasts you."
        description="NexSecure is an engineering-driven team based in Ernakulam, working across Kerala. If you care about doing installations properly — documented, tested, supportable — we'd like to meet you."
      />

      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="nx-wrap">
          <Reveal>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mb-12">Why NexSecure</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {WHY.map((w, i) => (
              <StaggerItem key={w.title}>
                <div className="h-full bg-nx-navy p-8" data-testid={`careers-why-${i}`}>
                  <span className="font-marcellus text-nx-gold/60 text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-marcellus text-lg text-white mt-4 mb-3">{w.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-nx-navy2 border-b border-white/10" data-testid="careers-openings">
        <div className="nx-wrap">
          <Reveal>
            <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Current Openings</span>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-3">Open roles</h2>
            <p className="text-base text-slate-400 mb-12 max-w-xl leading-relaxed">Roles open as we grow. Don&apos;t see an exact fit? Speculative applications are welcome — good engineers are always worth a conversation.</p>
          </Reveal>
          <div className="border-t border-white/10">
            {OPENINGS.map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.08}>
                <div className="group border-b border-white/10 py-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-start px-2 sm:px-5 hover:bg-white/[0.03] transition-colors" data-testid={`careers-opening-${o.slug}`}>
                  <span className="font-marcellus text-nx-gold/60 text-lg pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-marcellus text-xl sm:text-2xl text-white">{o.title}</h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1.5"><MapPin size={13} className="text-nx-goldlight" /> {o.location}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} className="text-nx-goldlight" /> {o.type}</span>
                    </div>
                    <p className="text-sm text-slate-400 mt-4 leading-relaxed max-w-2xl">{o.text}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAppForm(true)}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight border border-nx-gold/40 hover:border-nx-goldlight px-5 py-3 transition-colors shrink-0"
                    data-testid={`careers-apply-${o.slug}`}
                  >
                    Apply <ArrowRight size={13} />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="apply-now" className="relative overflow-hidden py-20 sm:py-28 bg-nx-navy scroll-mt-20" data-testid="careers-cta-block">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 62% 55% at 50% 42%, rgba(201,168,76,0.20), transparent 70%)" }} />
        <div className="nx-wrap relative text-center">
          <Reveal>
            <h2
              className="font-marcellus font-bold text-4xl sm:text-6xl lg:text-7xl text-nx-goldlight tracking-wide"
              style={{ textShadow: "0 1px 0 #8a6d2a, 0 2px 0 #7a5f24, 0 3px 0 #6b521f, 0 4px 0 #5c461a, 0 6px 2px rgba(0,0,0,0.55), 0 12px 28px rgba(201,168,76,0.35)" }}
              data-testid="careers-cta-heading"
            >
              Join the NexSecure Team
            </h2>
            <p className="text-white text-base sm:text-lg mt-6 max-w-xl mx-auto leading-relaxed" data-testid="careers-cta-subtext">
              Be part of Kerala&apos;s leading security technology company. Your career starts here.
            </p>
            <button
              type="button"
              onClick={() => setShowAppForm(true)}
              className="nx-cta-pulse mt-10 w-full sm:w-auto bg-nx-goldlight text-nx-navy font-bold text-lg sm:text-xl tracking-wide px-12 py-5 rounded-lg transition-transform duration-300 hover:scale-105"
              data-testid="careers-cta-apply"
            >
              Click Here to Apply Now
            </button>
          </Reveal>
        </div>
      </section>

      <section id="apply" className="py-16 sm:py-24 border-t border-white/10">
        <div className="nx-wrap max-w-xl mx-auto text-center">
          <Reveal>
            <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Apply</span>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-3">Send your application.</h2>
            <p className="text-base text-slate-400 mb-10 leading-relaxed">One detailed form, honest answers, documents attached. We respond within five working days.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-white/[0.03] p-8 text-left">
              <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer email?</p>
              <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-slate-400 mb-2"><Mail size={14} className="text-nx-goldlight" /> Careers inbox</div>
              <a href="mailto:careers@nexsecure.in" data-testid="careers-email-link" className="text-lg font-medium text-slate-100 hover:text-nx-goldlight transition-colors">careers@nexsecure.in</a>
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">Send your CV with the role in the subject line. We respond within five working days.</p>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand title="Not a job seeker?" text="If you landed here looking for infrastructure work — start with a site assessment." />
      <SiteFooter />
      <CareerApplicationForm open={showAppForm} onClose={() => setShowAppForm(false)} />
    </div>
  );
};

export default Careers;
